<template>
  <div class="p2p-stack" style="gap:24px">
    <div v-if="!member" class="p2p-card is-accent">
      <h2 class="p2p-h2">Create an account to publish</h2>
      <p class="p2p-text">Your sample posts stay with you. Connect X, LinkedIn, Instagram and more after signing up.</p>
      <router-link to="/register" class="p2p-btn" style="align-self:flex-start">Create my account</router-link>
    </div>
    <template v-else>
      <div class="p2p-between">
        <div class="p2p-stack" style="gap:6px">
          <h2 class="p2p-h2">Where should these go?</h2>
          <p class="p2p-muted" style="margin:0">Choose the accounts to publish to. Each post is adapted to what every network accepts.</p>
        </div>
        <span v-if="demo" class="p2p-chip warn" title="Development mode: connections and publishing are simulated">Demo connections</span>
      </div>
      <div v-if="!available" class="p2p-alert">Account connections are being set up. You can still review, copy and download your posts.</div>

      <div v-if="connected.length" class="p2p-grid" style="grid-template-columns:repeat(auto-fill,minmax(min(260px,100%),1fr))">
        <label v-for="account in connected" :key="account.id" :class="['p2p-card', 'tight', 'p2p-account', { 'is-accent': chosen.indexOf(account.id) >= 0 }]">
          <div class="p2p-row" style="flex-wrap:nowrap">
            <input type="checkbox" :value="account.id" v-model="chosen" class="p2p-account-check">
            <NetBadge :platform="account.platform" large />
            <div style="min-width:0">
              <div class="p2p-h3" style="overflow:hidden;text-overflow:ellipsis">{{ account.display_name || account.username }}</div>
              <div class="p2p-faint">{{ account.label }}<span v-if="account.username"> · {{ account.username }}</span></div>
            </div>
          </div>
        </label>
      </div>
      <div v-else class="p2p-empty">
        <strong>No accounts connected yet</strong>
        <span>Connect the accounts you post from. It takes a few seconds and you can disconnect at any time.</span>
      </div>

      <section class="p2p-stack">
        <h3 class="p2p-kicker">Connect an account</h3>
        <div class="p2p-row" style="gap:8px">
          <button v-for="platform in platforms" :key="platform" type="button" class="p2p-btn soft" :disabled="!available || connecting === platform" @click="connect(platform)">
            <NetBadge :platform="platform" /> {{ label(platform) }}
          </button>
        </div>
      </section>

      <section v-if="chosenAccounts.length && posts.length" class="p2p-card">
        <h3 class="p2p-h3">How each post fits</h3>
        <div style="overflow-x:auto">
          <table class="p2p-table">
            <thead><tr><th>Post</th><th v-for="account in chosenAccounts" :key="account.id"><NetBadge :platform="account.platform" /></th></tr></thead>
            <tbody>
              <tr v-for="post in posts" :key="post.id">
                <td style="max-width:280px"><div class="p2p-faint">{{ post.format }}</div><div style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ post.content.hook }}</div></td>
                <td v-for="account in chosenAccounts" :key="account.id">
                  <span v-if="cell(post, account).ok" class="p2p-chip good" :title="cell(post, account).warnings.join(' ')">{{ cell(post, account).warnings.length ? 'Adapted' : 'Ready' }}</span>
                  <span v-else class="p2p-chip" :title="cell(post, account).reason">Skipped</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="p2p-faint" style="margin:0">Skipped posts are not sent to that network (for example, Instagram needs an image). Hover a cell for details.</p>
      </section>

      <div class="p2p-sticky">
        <span v-if="posts.length || !loaded">{{ chosen.length }} account{{ chosen.length === 1 ? '' : 's' }} selected · {{ pairs }} publish{{ pairs === 1 ? '' : 'es' }}</span>
        <span v-else>{{ chosen.length }} account{{ chosen.length === 1 ? '' : 's' }} selected · no posts to publish now</span>
        <button type="button" class="p2p-btn lg" :disabled="!loaded || !chosen.length || (posts.length > 0 && !pairs)" @click="next">{{ posts.length || !loaded ? 'Publish options' : 'Set up autopilot' }} <v-icon small color="#0b0b10">mdi-arrow-right</v-icon></button>
      </div>
      <div v-if="error" class="p2p-alert bad">{{ error }}</div>
    </template>

    <v-dialog v-model="bluesky.open" max-width="480">
      <div class="p2p-card raised">
        <h2 class="p2p-h2">Connect Bluesky</h2>
        <p class="p2p-text">Bluesky uses an app password, never your account password. Create one in Bluesky under Settings, then App passwords.</p>
        <label class="p2p-label">Handle<input v-model="bluesky.handle" class="p2p-input" placeholder="you.bsky.social" autocomplete="off"></label>
        <label class="p2p-label">App password<input v-model="bluesky.password" class="p2p-input" type="password" autocomplete="off"></label>
        <div class="p2p-row">
          <button type="button" class="p2p-btn" :disabled="!bluesky.handle || !bluesky.password" @click="connect('bluesky', true)">Connect</button>
          <button type="button" class="p2p-btn ghost" @click="bluesky.open = false">Cancel</button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import NetBadge, { LABELS } from '../NetBadge.vue';
import { get, post, patch, message } from '../../api';

export const PLATFORM_ORDER = ['x', 'linkedin', 'bluesky', 'instagram', 'facebook', 'threads', 'pinterest', 'tiktok', 'youtube'];

export default {
  name: 'StepAccounts',
  components: { NetBadge },
  props: { project: { type: Object, required: true }, config: { type: Object, default: null }, member: { type: Boolean, default: false } },
  data: () => ({ chosen: [], posts: [], loaded: false, connecting: '', error: '', available: true, demo: false,
    bluesky: { open: false, handle: '', password: '' }, platforms: PLATFORM_ORDER }),
  computed: {
    connected() { return this.$store.getters['publisher/connected']; },
    chosenAccounts() { return this.connected.filter(account => this.chosen.indexOf(account.id) >= 0); },
    pairs() {
      let count = 0;
      this.posts.forEach(item => this.chosenAccounts.forEach(account => { if (this.cell(item, account).ok) count++; }));
      return count;
    },
    batchId() { return this.$route.query.batch || (this.project.batches && this.project.batches.length ? String(this.project.batches[0].id) : ''); }
  },
  async mounted() {
    if (!this.member) return;
    try {
      const result = await this.$store.dispatch('publisher/loadAccounts');
      this.available = result.available;
      this.demo = result.demo;
      const saved = (this.project.settings && this.project.settings.account_ids) || [];
      this.chosen = saved.filter(id => this.connected.some(account => account.id === id));
      if (!this.chosen.length && this.connected.length) this.chosen = this.connected.map(account => account.id);
      if (this.batchId) {
        const data = await get('/projects/' + this.project.id + '/posts', { batch_id: this.batchId });
        this.posts = data.posts.filter(item => item.selected);
      }
    } catch (error) {
      this.error = message(error);
    } finally {
      this.loaded = true;
    }
  },
  methods: {
    label(platform) { return LABELS[platform] || platform; },
    cell(item, account) { return item.networks[account.platform] || { ok: false, reason: 'Not supported', warnings: [] }; },
    async connect(platform, confirmed) {
      if (platform === 'bluesky' && !confirmed) { this.bluesky.open = true; return; }
      this.connecting = platform;
      this.error = '';
      try {
        const payload = { platform };
        if (platform === 'bluesky') { payload.handle = this.bluesky.handle; payload.app_password = this.bluesky.password; }
        const result = await post('/social/connect', payload);
        sessionStorage.setItem('p2p-connect-return', this.$route.fullPath);
        window.location.href = result.url;
      } catch (error) {
        this.error = message(error);
        this.connecting = '';
      }
    },
    async next() {
      try {
        const settings = { ...this.project.settings, account_ids: this.chosen };
        // The next step reads the chosen accounts from the project, so hand it the saved one straight away.
        this.$emit('project', await patch('/projects/' + this.project.id, { settings }));
      } catch (error) {
        this.error = message(error);
        return;
      }
      // Without posts there is nothing to publish now, so the next step opens on autopilot.
      this.$emit('go', 'publish', this.posts.length ? { batch: this.batchId } : { tab: 'automate' });
    }
  }
};
</script>

<style>
.p2p-account{cursor:pointer}
.p2p-account-check{width:18px;height:18px;accent-color:#d6f34b;flex:0 0 auto}
</style>
